import "./polyfills";
import "./utils/ConsoleManager";
import "./index.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { howlerAudio } from "./utils/howlerAudio";

console.info("🚀 Application starting...");

// Function to ensure Web Audio API is ready
const initializeWebAudio = () => {
  try {
    // Create an AudioContext to test and initialize
    const audioContext = new (window.AudioContext || 
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    
    // Log the state
    console.info(`🔊 AudioContext created, state: ${audioContext.state}`);
    
    // If suspended, we need to resume it on user interaction
    if (audioContext.state === 'suspended') {
      console.info('🔊 AudioContext is suspended, will resume on user interaction');
      
      const resumeAudioContext = () => {
        audioContext.resume().then(() => {
          console.info(`🔊 AudioContext resumed, state: ${audioContext.state}`);
        }).catch(err => {
          console.error('🔊 Error resuming AudioContext:', err);
        });
        
        // Cleanup event listeners
        ['touchstart', 'touchend', 'mousedown', 'keydown'].forEach(eventType => {
          document.documentElement.removeEventListener(eventType, resumeAudioContext);
        });
      };
      
      // Add event listeners to resume audio context on user interaction
      ['touchstart', 'touchend', 'mousedown', 'keydown'].forEach(eventType => {
        document.documentElement.addEventListener(eventType, resumeAudioContext, true);
      });
    } else {
      console.info('🔊 AudioContext is already running');
    }
    
    return audioContext;
  } catch (err) {
    console.error('🔊 Error initializing Web Audio API:', err);
    return null;
  }
};

// Initialize Web Audio API
const audioContext = initializeWebAudio();

// Initialize Howler audio system with explicit Web Audio preference
howlerAudio.initialize();
console.info("🔊 Audio system initialized with Howler.js");

// Make the audio context available for debugging
if (audioContext) {
  (window as unknown as { debugAudioContext?: AudioContext }).debugAudioContext = audioContext;
  console.info("🔊 AudioContext available for debugging as window.debugAudioContext");
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);
